import { brokerRedirect } from '@organization/app-constants'
import {
  AgentsStaffPage,
  ApplicationPage,
  CompaniesPartnerManagementPage,
  ConfigProfilePage,
  ConfigsMFAPage,
  DashboardPage,
  LockoutsPage,
  LogsApplicationsPage,
  LogsMFAPage,
  LogsPage,
  MediaStaffPage,
  NotFound,
  NotificationsDetailsPage,
  NotificationsPage,
  PermissionsUserFormPage,
  StaffPartnerPage,
  TeamDetails,
  TeamsStaffPage,
  Theme,
  TrustedDevicesPage,
  UserDetails,
  UserInviteWizard,
  UserProfilePage,
  UsersPage
} from '@organization/features'
import {
  ConfirmBrokerInvitation,
  ConfirmMFABackUpCode,
  ConfirmMFAOtpCode,
  InvitationForm,
  LoginForm,
  ResetPasswordConfirmForm,
  ResetPasswordForm
} from '@organization/features/authentication/pages'
import { AuthGuard } from '@organization/hocs'
import { useAppConfig, useSearchParams } from '@organization/hooks'
import { AppLayout } from '@organization/layout/AppLayout'
import { LoadingScreen } from '@shared/components'
import { useSessionContext } from '@shared/hooks'
import { Suspense } from 'react'
import { Navigate, Outlet, RouteObject, useRoutes } from 'react-router-dom'

import { paths } from './paths'

export const Router = () => {
  const matchedConfig = useAppConfig()
  const params = useSearchParams()
  const { isAdmin } = useSessionContext()

  const routes: RouteObject[] = [
    {
      element: (
        <Suspense fallback={<LoadingScreen />}>
          <Outlet />
        </Suspense>
      ),
      children: [
        {
          path: 'authentication',
          element: <Outlet />,
          children: [
            {
              path: paths.auth.login,
              element: <LoginForm />
            },
            {
              path: paths.auth.resetPassword,
              element: <ResetPasswordForm />
            },
            {
              path: paths.auth.resetPasswordConfirm,
              element: <ResetPasswordConfirmForm />
            },
            {
              path: paths.auth.invitation,
              element: <InvitationForm />
            },
            {
              path: paths.auth.brokerConfirmInvitation,
              element: <ConfirmBrokerInvitation />
            },
            {
              path: paths.auth.otpConfirm,
              element: <ConfirmMFAOtpCode />
            },
            {
              path: paths.auth.backUpConfirm,
              element: <ConfirmMFABackUpCode />
            }
          ]
        },
        {
          path: '/',
          element: (
            <AuthGuard>
              <AppLayout>
                <Outlet />
              </AppLayout>
            </AuthGuard>
          ),
          children: [
            ...(!matchedConfig.forceToken
              ? [
                  { path: paths.applications, element: <ApplicationPage /> },
                  { path: paths.dashboard, element: <DashboardPage /> },
                  { path: paths.people(':tab'), element: <UsersPage /> },
                  {
                    path: paths.partners.companiesManagement,
                    element: <CompaniesPartnerManagementPage />
                  },
                  {
                    path: paths.partners.root,
                    element: <StaffPartnerPage />,
                    children: [
                      {
                        path: paths.partners.agents(':id'),
                        element: <AgentsStaffPage />
                      },
                      {
                        path: paths.partners.teams(':id'),
                        element: <TeamsStaffPage />
                      },
                      {
                        path: paths.partners.media(':id'),
                        element: <MediaStaffPage />
                      }
                    ]
                  },
                  {
                    path: paths.users.invite,
                    element: <UserInviteWizard />
                  },
                  {
                    path: '/users/permissions',
                    element: <PermissionsUserFormPage />
                  },
                  {
                    path: paths.users.permissions(':userId'),
                    element: <PermissionsUserFormPage />
                  },
                  {
                    path: paths.users.details(':userId'),
                    element: <UserDetails />
                  },
                  {
                    path: paths.teams.details(':teamId'),
                    element: <TeamDetails />
                  },
                  {
                    path: paths.logs.root,
                    element: <LogsPage />,
                    children: [
                      {
                        path: paths.logs.applications,
                        element: <LogsApplicationsPage />
                      },
                      {
                        path: paths.logs.mfa,
                        element: <LogsMFAPage />
                      },
                      {
                        path: paths.logs.root,
                        element: (
                          <Navigate
                            to={
                              isAdmin ? paths.logs.applications : paths.logs.mfa
                            }
                            replace
                          />
                        )
                      }
                    ]
                  },
                  {
                    path: paths.notifications.root,
                    element: <NotificationsPage />
                  },
                  {
                    path: paths.notifications.details(':id'),
                    element: <NotificationsDetailsPage />
                  },
                  {
                    path: paths.lockouts.root,
                    element: <LockoutsPage />
                  },
                  {
                    path: paths.configsMFAPage.root,
                    element: <ConfigsMFAPage />
                  }
                ]
              : []),

            {
              element: <UserProfilePage />,
              children: [
                { path: paths.profile.root, element: <ConfigProfilePage /> },
                {
                  path: paths.profile.trustedDevices,
                  element: <TrustedDevicesPage />
                }
              ]
            },
            {
              path: paths.theme,
              element: <Theme />
            },

            ...(matchedConfig && [
              {
                path: matchedConfig.path,
                element: matchedConfig.routes
              }
            ])
          ]
        },

        // Todo: -> remove after change confirm url
        {
          path: '/broker/confirm-invite',
          element: (
            <Navigate
              to={{
                pathname: paths.auth.brokerConfirmInvitation,
                search: params.toString()
              }}
              replace
            />
          )
        },

        {
          path: '*',
          element: matchedConfig.forceToken ? (
            <Navigate to={brokerRedirect(matchedConfig.forceToken)} />
          ) : (
            <NotFound />
          )
        }
      ]
    }
  ]

  return useRoutes(routes)
}
